import { NOT_SAVED_ID } from "app/model"
import { getFormatById } from "format/tools/common"
import { startMediaListeners } from "media/listeners"
import {
  ICampaignMediaVideo,
  ICreateMediaParams,
  IMedia,
  IMediaCreativeForecastInfo,
  IMediaDTO,
  IMediaInfo,
  IMediaVideoCreatParams,
  MEDIA_CONTENT_TYPES,
  MEDIA_SIMPLE_TYPES,
  MEDIA_TYPES
} from "media/model"
import { mediaRPO } from "media/repository"
import { state } from "media/state"
import { getUserAgency } from "user/tools/common"
import { getContentTypeId, getMediaTypeId, isImageContentType } from "./media-type"

startMediaListeners()

export function getCampaignMedias(campaignId: number): Promise<IMedia[]> {
  return mediaRPO.getCampaignMedias(campaignId)
}

export const getMediasById = (ids: number[]): Promise<IMediaDTO[]> => mediaRPO.getMediasById(ids)

export async function createCampaignMedia({
  campaignId,
  mediaName,
  formatId,
  width,
  height,
  isVideo
}: ICreateMediaParams): Promise<IMedia> {
  const agencyId = getUserAgency().id
  const media = await mediaRPO.createMedia({
    name: mediaName,
    agency_id: agencyId === null ? undefined : agencyId,
    format_id: typeof formatId === "number" ? formatId : undefined,
    width: typeof width === "number" ? width : undefined,
    height: typeof height === "number" ? height : undefined,
    media_type_id: getMediaTypeId(MEDIA_TYPES.DIGITAL_BILLBOARD) as 1 | 2 | 3 | 5 | 6 | 7 | 8 | 9,
    content_type_id:
      typeof isVideo === "boolean"
        ? getContentTypeId(isVideo ? MEDIA_CONTENT_TYPES.VIDEO : MEDIA_CONTENT_TYPES.IMAGE)
        : getContentTypeId(MEDIA_CONTENT_TYPES.VIDEO),
    is_draft: true
  })
  const campaignMedia = await mediaRPO.createCampaignMedia(campaignId, media.id)
  media.campaignMediaId = campaignMedia.id
  media.approval_status_id = campaignMedia.approval_status_id
  return media
}

export function deleteCampaignMedia(campaignId: number, mediaId: number): Promise<void> {
  return mediaRPO.deleteCampaignMedia(campaignId, mediaId)
}

export function getMediaCreativeForecastIdFromVideo(video: ICampaignMediaVideo): string {
  return getMediaCreativeForecastId(MEDIA_SIMPLE_TYPES.VIDEO, null, video.width as number, video.height as number)
}

export function getMediaType(media: IMediaDTO): MEDIA_SIMPLE_TYPES {
  return isImageContentType(media.content_type_id) ? MEDIA_SIMPLE_TYPES.IMAGE : MEDIA_SIMPLE_TYPES.VIDEO
}

export function getMediaCreativeForecastIdFromMedia(media: IMedia): string {
  const type = getMediaType(media)
  const formatId = typeof media.format_id === "number" ? media.format_id : null
  const width = typeof media.width === "number" ? media.width : null
  const height = typeof media.height === "number" ? media.height : null
  if (type === MEDIA_SIMPLE_TYPES.VIDEO && formatId === null && width === null && height === null) {
    const video = media.videos[0]
    if (video) {
      return getMediaCreativeForecastId(type, null, video.width as number, video.height as number)
    }
  }
  return getMediaCreativeForecastId(type, formatId, width, height)
}

export function getMediaCreativeForecastIdFromForecast(forecast: IMediaCreativeForecastInfo): string {
  return getMediaCreativeForecastId(
    forecast.mediaType,
    typeof forecast.formatId === "number" ? forecast.formatId : null,
    typeof forecast.width === "number" ? forecast.width : null,
    typeof forecast.height === "number" ? forecast.height : null
  )
}

export async function createMediaVideo(params: IMediaVideoCreatParams): Promise<ICampaignMediaVideo> {
  return mediaRPO.createMediaVideo(params)
}

export function getMediaCreativeForecastInfoFromId(forecastId: string): IMediaCreativeForecastInfo {
  const [type, formatId, width, height] = forecastId.split("-")
  return {
    forecastId,
    mediaType: type as MEDIA_SIMPLE_TYPES,
    formatId: (formatId === "0" || formatId) && formatId !== "null" ? Number(formatId) : null,
    width: (width === "0" || width) && width !== "null" ? Number(width) : NOT_SAVED_ID,
    height: (height === "0" || height) && height !== "null" ? Number(height) : NOT_SAVED_ID
  }
}

export function getMediaCreativeForecastId(
  mediaType: MEDIA_SIMPLE_TYPES,
  formatId: number | null,
  width: number | null,
  height: number | null
): string {
  return `${mediaType}-${formatId}-${width}-${height}`
}

export function getMediaFormat(media: IMedia): [number, number] | undefined {
  if (media.format_id !== null) {
    const format = getFormatById(media.format_id)
    if (format) {
      return [format.width as number, format.height as number]
    }
  }
  if (media.width !== null && media.height !== null) {
    return [media.width, media.height]
  }
}

export function getDisplayMediaFormat(media: IMedia): string {
  const format = getMediaFormat(media)
  if (!format) {
    return "-"
  }
  return `${format[0]} x ${format[1]} px`
}

export const getCachedMediaInfo = async (mediaId: number): Promise<IMediaInfo> => {
  const mediaInfo = state.mediaInfoById[mediaId]
  if (mediaInfo !== undefined) {
    return mediaInfo
  }
  try {
    const medias = await getMediasById([mediaId])
    if (medias.length === 0) {
      state.mediaInfoById[mediaId] = {
        name: "-",
        campaignMediaId: null
      }
    } else {
      const media = medias[0]
      const campaignMedia = media.campaign_media ? media.campaign_media[0] : undefined
      state.mediaInfoById[mediaId] = {
        name: media.name || "-",
        campaignMediaId: campaignMedia === undefined ? null : campaignMedia.id || null
      }
    }
  } catch (error) {
    console.error(error)
    state.mediaInfoById[mediaId] = {
      name: "-",
      campaignMediaId: null
    }
  }
  return state.mediaInfoById[mediaId]
}
